import {
  AccountLimitsRail,
  Box,
  withStyles,
  capitalizeFirstLetter,
  getCSSVariable,
  PulloverSelect,
  Tooltip,
  VelocityRail,
} from 'vopay';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TabButton from '../../components/TabButton';
import styles from './AccountLimits.module.scss';
import { BodyText, Button, Skeleton, Title } from 'UI';
import AccountLimit, { Direction } from './AccountLimit';
import useAccountLimitLabels from './useAccountLimitLabels';
import { AccountLimits as AccountLimitsType } from '@/hooks/useApi/useFetchAccountLimits';

const cn = withStyles(styles);

const RAIL_DISPLAY_ORDER: VelocityRail[] = [
  'eft',
  'interac',
  'rtr',
  'credit_card',
  'push_to_card',
  'ach',
  'rtp',
  'fednow',
  'wire',
];

const DIRECTIONS: Direction[] = ['Inbound', 'Outbound'];

// 80–99% gauge colour
const AMBER = '#F5A623';

type AccountLimitsProps = {
  accountLimits: AccountLimitsType | null;
  currency: string;
  loading: boolean;
  hasError: boolean;
  onRetry: () => void;
};

type Tab = 'daily' | 'weekly' | 'monthly';
type LimitType = 'Daily' | 'Weekly' | 'Monthly';

// "N/A" (no limit for the period) shows as a 0.00 gauge.
function parseAmount(value: string | undefined | null): string {
  const num = Number(value);

  return isNaN(num) ? '0.00' : String(num);
}

export default function AccountLimits({ accountLimits, currency, loading, hasError, onRetry }: AccountLimitsProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'HomepageRevamp.Widgets.AccountLimits' });
  const { railLabel } = useAccountLimitLabels();
  const [selectedRail, setSelectedRail] = useState<string>();
  const [activeTab, setActiveTab] = useState<Tab>('daily');
  const limitType = capitalizeFirstLetter(activeTab) as LimitType;
  const primaryColor = getCSSVariable('--primaryColor');
  const isRailMode = accountLimits?.VelocityScope === 'rail';

  const rails = useMemo(() => {
    if (accountLimits?.VelocityScope !== 'rail') return [];

    // Rails not in RAIL_DISPLAY_ORDER go last, labelled with the uppercased key and Inbound/Outbound titles.
    const keys = Object.keys(accountLimits.Rails);
    const known: string[] = RAIL_DISPLAY_ORDER.filter((rail) => keys.includes(rail));

    return [...known, ...keys.filter((key) => !known.includes(key))];
  }, [accountLimits]);

  const activeRail = selectedRail && rails.includes(selectedRail) ? selectedRail : rails[0];
  const activeRailLimits = isRailMode && activeRail ? accountLimits.Rails[activeRail] : undefined;
  const numRailDirections = DIRECTIONS.filter((direction) => activeRailLimits?.[direction]).length;

  const options: { label: string; value: Tab }[] = [
    { label: t('daily', { defaultValue: 'Daily' }), value: 'daily' },
    { label: t('weekly', { defaultValue: 'Weekly' }), value: 'weekly' },
    { label: t('monthly', { defaultValue: 'Monthly' }), value: 'monthly' },
  ];

  return (
    <>
      <Box ai="center" jc="sb" mb="md" height="auto">
        <Box ai="center" gapSize="8px">
          <Title level="5">{t('title', { defaultValue: 'Account Limits' })}</Title>

          {/* Before the first response the scope is unknown, so the selector skeleton shows in both modes */}
          {loading && (isRailMode || !accountLimits) ? (
            <Skeleton width={120} height={24} />
          ) : (
            <>
              {isRailMode && rails.length > 1 && (
                <PulloverSelect name="rail" value={activeRail} onChange={setSelectedRail}>
                  {rails.map((rail) => (
                    <PulloverSelect.Option key={rail} value={rail}>
                      {railLabel(rail)}
                    </PulloverSelect.Option>
                  ))}
                </PulloverSelect>
              )}

              {isRailMode && rails.length === 1 && (
                <BodyText weight="md" className={cn('railName')}>
                  {railLabel(rails[0])}
                </BodyText>
              )}
            </>
          )}

          {isRailMode && (
            <Tooltip
              information={
                <BodyText>
                  {t('railModeInfo', {
                    defaultValue:
                      'Limits apply separately to each payment rail. Fund limits are monitored; Withdraw limits are enforced — transactions above the limit are declined.',
                  })}
                </BodyText>
              }
              icon="info"
              position="bottom"
            />
          )}
        </Box>

        <TabButton disabled={loading} options={options} value={activeTab} onSelect={setActiveTab} />
      </Box>

      <div className={cn('accountLimitsContainer', isRailMode && numRailDirections === 1 && 'single')}>
        {loading ? (
          <>
            <AccountLimitSkeleton />
            <AccountLimitSkeleton />
          </>
        ) : hasError ? (
          <div className={cn('message')}>
            <BodyText>{t('loadError', { defaultValue: 'Could not load account limits.' })}</BodyText>

            <Button size="small" onClick={onRetry}>
              {t('retry', { defaultValue: 'Retry' })}
            </Button>
          </div>
        ) : isRailMode ? (
          activeRail ? (
            DIRECTIONS.map((direction) => {
              const limits = activeRailLimits?.[direction];

              return (
                limits && (
                  <RailLimit
                    key={direction}
                    rail={activeRail}
                    direction={direction}
                    limits={limits}
                    limitType={limitType}
                    currency={accountLimits.Currency ?? currency}
                  />
                )
              );
            })
          ) : (
            <BodyText className={cn('message')}>
              {t('noRailLimits', { defaultValue: 'No payment rail limits configured.' })}
            </BodyText>
          )
        ) : accountLimits ? (
          <>
            <AccountLimit
              type="Inbound"
              title={t('fundLimit', { defaultValue: 'Fund Limit' })}
              remainingAmount={t('remainingAmount', { defaultValue: 'Remaining Amount' })}
              startingAmount={t('startingAmount', { defaultValue: 'Starting Amount' })}
              remaining={parseAmount(accountLimits.Inbound.RemainingLimits[limitType])}
              total={parseAmount(accountLimits.Inbound.Limits[limitType])}
              currency={currency}
              strokeColor={primaryColor}
            />

            <AccountLimit
              type="Outbound"
              title={t('withdrawLimit', { defaultValue: 'Withdraw Limit' })}
              remainingAmount={t('remainingAmount', { defaultValue: 'Remaining Amount' })}
              startingAmount={t('startingAmount', { defaultValue: 'Starting Amount' })}
              remaining={parseAmount(accountLimits.Outbound.RemainingLimits[limitType])}
              total={parseAmount(accountLimits.Outbound.Limits[limitType])}
              currency={currency}
              strokeColor={primaryColor}
            />
          </>
        ) : null}
      </div>
    </>
  );
}

function AccountLimitSkeleton() {
  return (
    <div className={cn('accountLimitsColumn')}>
      <Skeleton width={100} height={20} />
      <Skeleton circle width={180} height={180} />
      <Skeleton width={140} height={44} />
      <Skeleton width={140} height={44} />
    </div>
  );
}

type RailLimitProps = {
  rail: string;
  direction: Direction;
  limits: NonNullable<AccountLimitsRail[Direction]>;
  limitType: LimitType;
  currency: string;
};

function RailLimit({ rail, direction, limits, limitType, currency }: RailLimitProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'HomepageRevamp.Widgets.AccountLimits' });
  const { directionTitle } = useAccountLimitLabels();
  const total = limits.Limits[limitType];
  const remaining = limits.RemainingLimits[limitType];
  const hasLimit = total !== 'N/A';
  const isUsedUp = hasLimit && Number(remaining) <= 0;
  // Rounded like ProgressGraph's label, so 999.99 of 1,000 reads "100%" and is red, not amber.
  const usedPercent = Math.round((1 - Number(remaining) / Number(total)) * 10000) / 100;

  function thresholdColor() {
    if (usedPercent >= 100) return getCSSVariable('--vopayRed');
    if (usedPercent >= 80) return AMBER;

    return getCSSVariable('--primaryColor');
  }

  return (
    <AccountLimit
      type={direction}
      title={directionTitle(rail, direction)}
      remainingAmount={t('remainingAmount', { defaultValue: 'Remaining Amount' })}
      startingAmount={t('startingAmount', { defaultValue: 'Starting Amount' })}
      remaining={parseAmount(remaining)}
      total={parseAmount(total)}
      currency={currency}
      strokeColor={thresholdColor()}
      limitReachedText={
        !isUsedUp
          ? undefined
          : direction === 'Inbound'
            ? t('limitExceeded', { defaultValue: 'Limit exceeded — contact your account manager' })
            : t('limitReached', { defaultValue: 'Limit reached' })
      }
    />
  );
}
